import { Typography } from '@euler/app/components/typography';
import {
  useAiChatActions,
  useAiChatMessage,
} from '@euler/app/flows/ai-suite/chat/AiChatActions';
import { ChatCard } from '@euler/app/flows/ai-suite/components/ChatCard';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  CoachingDifficulty,
  kCoachingDifficulties,
} from '@euler/model/coaching';
import { Check, SlidersHorizontal } from 'lucide-react-native';
import { memo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { kCaseLevelHints, kLevelNames, kStoryLevelHints } from '../copy';

/** The levels on offer; a case's other levels are not shown at all. */
export const DifficultyCard = memo(
  ({
    subject,
    levels,
  }: {
    subject: 'case' | 'story';
    levels: CoachingDifficulty[];
  }) => {
    const { rpx } = useResponsiveContext();
    const chat = useAiChatActions();
    const { latest } = useAiChatMessage();
    const [chosen, setChosen] = useState<CoachingDifficulty | null>(null);
    const hints = subject === 'case' ? kCaseLevelHints : kStoryLevelHints;
    return (
      <ChatCard
        icon={SlidersHorizontal}
        color={aiColors.primary}
        title="难度"
        subtitle="难的是藏得多、限制多，不是客户难缠"
        tinted
      >
        <View>
          {kCoachingDifficulties
            .filter(level => levels.includes(level))
            .map(level => {
              const on = chosen === level;
              const enabled = latest && !chosen && !chat.busy;
              return (
                <Pressable
                  key={level}
                  disabled={!enabled}
                  onPress={() => {
                    if (chat.send(kLevelNames[level])) setChosen(level);
                  }}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: on, disabled: !enabled }}
                  style={{
                    flexDirection: 'row',
                    gap: rpx(12),
                    padding: rpx(14),
                    borderTopWidth: 1,
                    borderTopColor: aiColors.border,
                    backgroundColor: on
                      ? tint(aiColors.green, 0.12)
                      : undefined,
                  }}
                >
                  <View
                    style={{
                      width: rpx(16),
                      height: rpx(16),
                      marginTop: rpx(2),
                      borderRadius: rpx(8),
                      borderWidth: on ? 0 : 1,
                      borderColor: aiColors.border,
                      backgroundColor: on ? aiColors.primary : undefined,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {on ? <Check size={rpx(10)} color="#fff" /> : null}
                  </View>
                  <View style={{ flex: 1 }}>
                    <Typography.Text
                      size={rpx(14)}
                      bold
                      color={aiColors.foreground}
                    >
                      {kLevelNames[level]}
                    </Typography.Text>
                    <Typography.Text
                      size={rpx(11)}
                      color={aiColors.mutedForeground}
                      lineHeight={rpx(17)}
                    >
                      {hints[level]}
                    </Typography.Text>
                  </View>
                </Pressable>
              );
            })}
        </View>
      </ChatCard>
    );
  },
);
